import axios from 'axios';
import _ from 'lodash';
import store from 'app/store';
import { getProducersQuery, getSplitProducerQuery } from './queries';
import { arrayToMap } from '../../utils';
import { requiresProducerBusinessData } from './utils/regionUtils';
import { buildExpressQuery } from './queries/builders';

const getSplitProducerListDetails = async () => {
    try {
        // Get the current region from the store
        const { region } = store.getState().persisted.auth.user.data;

        // Check if producer business data is needed for this region
        const needsProducerBusinessData = requiresProducerBusinessData(region);

        // Fetch split producer data
        const splitProducerQuery = getSplitProducerQuery({
            fields: ['parent_producer', 'child_producer', 'split_producer_agreement_details', 'start_date', 'end_date', 'payment_type'],
        });
        const splitProducerResponse = await axios.get(splitProducerQuery);
        const splitProducers = splitProducerResponse.data;

        // If producer business data is not needed for this region, return early
        if (!needsProducerBusinessData) {
            return { splitProducers };
        }

        // Extract parent and child producer IDs
        const parentProducerIds = [];
        const childProducerIds = [];

        splitProducers.forEach((splitProducer) => {
            if (splitProducer.parent_producer) {
                parentProducerIds.push(splitProducer.parent_producer.id);
            }
            if (splitProducer.child_producer) {
                childProducerIds.push(splitProducer.child_producer.id);
            }
        });

        // Combine all producer IDs and remove duplicates
        const producerIds = _.uniq([...parentProducerIds, ...childProducerIds]);

        // Only fetch producer data if there are IDs to fetch
        let producerMap = {};
        if (producerIds.length > 0) {
            // Fetch producers with populated producer business data
            const producersQuery = getProducersQuery({
                fields: ['id', 'name'],
                ids: producerIds,
                populate: {
                    producer_business: ['name', 'license_number'],
                },
                queryBuilder: buildExpressQuery,
            });

            const producersResponse = await axios.get(producersQuery);
            producerMap = arrayToMap(producersResponse.data, '_id');
        }

        // Add producer business data to each split producer
        const enhancedSplitProducers = splitProducers.map((splitProducer) => {
            const parentProducer = producerMap[splitProducer.parent_producer.id] || {};
            const childProducer = producerMap[splitProducer.child_producer.id] || {};

            // Get producer business data from the populated producer objects
            const parentProducerBusiness = parentProducer.producer_business || {};
            const childProducerBusiness = childProducer.producer_business || {};

            return {
                ...splitProducer,
                parent_producer_business_name: parentProducerBusiness.name || '',
                parent_producer_business_license_number: parentProducerBusiness.license_number || '',
                child_producer_business_name: childProducerBusiness.name || '',
                child_producer_business_license_number: childProducerBusiness.license_number || '',
            };
        });

        return { splitProducers: enhancedSplitProducers };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        throw new Error('Unable to process request.');
    }
};

export default getSplitProducerListDetails;
